import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import styled from 'styled-components';
import { BulkUploadWizardShell } from './BulkUploadWizardShell';
import { MarkingTemplatesFeedbackType } from '../../CreateNewTask/components/MarkingTemplatesFeedbackType';
import { CommentBanksFeedbackType } from '../../CreateNewTask/components/CommentBanksFeedbackType';
import { SuccessCriteriaFeedbackType } from '../../CreateNewTask/components/SuccessCriteriaFeedbackType';
import { ShortAnswerFeedbackType } from '../../CreateNewTask/components/ShortAnswerFeedbackType';
import { AdvancedMarkingControlsForm } from '../../AdvancedMarkingControls/ControlsForm';
import { ADVANCED_MARKING_DEFAULTS } from '../../../utils/advancedMarking';
import TaskCourseMaterialsCard from '../../CourseMaterials/TaskCourseMaterialsCard';
import {
  recommendMarkingTools,
  applyRecommendationsToForm,
  RECOMMENDABLE_TOOL_TYPES,
  questionHasStimulus,
  resolveRecommendationMarks,
} from '../../CreateNewTask/utils/toolRecommendation';
import { FEEDBACK_TYPE_IDS, isFeedbackTypeVisibleForContext } from '../../CreateNewTask/utils/constants';
import CommentBankDialog from '../../Shared/Dialogs/commentBank';
import SuccessCriteriaDialog from '../../Shared/Dialogs/successCriteria';
import ShortAnswerDialog from '../../Shared/Dialogs/shortAnswer';
import PreviewDialog from '../../Shared/Dialogs/preview/previewCard';
import {
  useMarkingCriterias,
  useCommentBankPreferences,
  useSuccessCriteria,
  useShortAnswers,
} from '../../state/hooks';
import jeddaiIcon from '../../../static/icons/black-generate-icon.svg';
import markingIcon from '../../../static/icons/select-marking-icon.svg';
import submitIcon from '../../../static/icons/submit-icon.svg';
import banksIcon from '../../../static/icons/banks.svg';
import darkCheckCircleIcon from '../../../static/icons/dark-check-circle.svg';
import shortAnswerIcon from '../../../static/icons/ShortAnsIcon.svg';
import { trackMarkingToolPicked } from '../../../utils/productEventMappers';
import { track as trackProductEvent } from '../../../utils/productEvents';

const METHOD_COPY =
  'JeddAI will use the most relevant rubrics and comment banks available to provide feedback.';

const EMPTY_FORM = {
  markingCriteria: null,
  commentBankId: '',
  commentBank: null,
  successCriteriaIds: [],
  shortAnswerIds: [],
};

const MARKING_SELECTION_DEFAULTS = {
  view: 'method',
  method: null,
  activeTab: 'general',
  formData: EMPTY_FORM,
  advancedControls: ADVANCED_MARKING_DEFAULTS,
  courseMaterials: [],
};

/**
 * The marking picks, seeded from the flow and reported back to it.
 *
 * They used to be five useStates in this component, which meant Back unmounted
 * them: a teacher who stepped back to re-read one response came forward to an
 * empty method chooser and rebuilt a selection they had already made. Holding
 * them here — one object, one owner — is what lets the flow keep them across
 * that round trip without this step knowing anything about steps.
 *
 * Reported from an effect rather than from inside the updater: the flow's setter
 * is a side effect, and running it during a state update is exactly what React
 * double-invokes in strict mode.
 */
const useMarkingSelection = (selection, onSelectionChange) => {
  const [state, setState] = useState(() => ({
    ...MARKING_SELECTION_DEFAULTS,
    ...selection,
  }));

  const reportRef = useRef(onSelectionChange);
  reportRef.current = onSelectionChange;
  useEffect(() => {
    reportRef.current?.(state);
  }, [state]);

  // One setter shape for all five fields, so a new pick cannot be added with a
  // setter that forgets to preserve the other four.
  const setField = useCallback(
    (key) => (value) =>
      setState((prev) => {
        const next = typeof value === 'function' ? value(prev[key]) : value;
        return Object.is(next, prev[key]) ? prev : { ...prev, [key]: next };
      }),
    [],
  );

  return { ...state, setField };
};

const normalizeList = (data) => {
  if (Array.isArray(data)) return data;
  if (Array.isArray(data?.content)) return data.content;
  if (Array.isArray(data?.items)) return data.items;
  if (Array.isArray(data?.criteria)) return data.criteria;
  return [];
};

export const BulkUploadMarkingRubricsStep = ({
  currentStep = 5,
  steps,
  onStepClick,
  draftSettings = {},
  fileCount = 0,
  onBack,
  onClose,
  onSubmit,
  submitLabel = 'Submit now',
  isSubmitting = false,
  fullPage = false,
  rubrics: rubricsProp,
  commentBanks: commentBanksProp,
  successCriteria: successCriteriaProp,
  shortAnswers: shortAnswersProp,
  // The picks themselves live in the flow — see useMarkingSelection.
  selection,
  onSelectionChange,
}) => {
  const { data: markingCriteriaData } = useMarkingCriterias();
  const { data: commentBanksData } = useCommentBankPreferences();
  const { data: successCriteriaData } = useSuccessCriteria();
  const { data: shortAnswersData } = useShortAnswers();

  const rubrics = useMemo(() => {
    const list = rubricsProp ?? normalizeList(markingCriteriaData);
    const filtered = list.filter(
      (item) =>
        !item?.id ||
        item.id === 'no_marking_criteria' ||
        item.type === 'RUBRICS' ||
        item.type === 'Rubrics',
    );
    return filtered;
  }, [rubricsProp, markingCriteriaData]);

  const commentBanks = useMemo(() => {
    const list = commentBanksProp ?? normalizeList(commentBanksData);
    return list;
  }, [commentBanksProp, commentBanksData]);

  const successCriteria = useMemo(() => {
    const list = successCriteriaProp ?? normalizeList(successCriteriaData);
    return list;
  }, [successCriteriaProp, successCriteriaData]);

  const shortAnswers = useMemo(() => {
    const list = shortAnswersProp ?? normalizeList(shortAnswersData);
    return list;
  }, [shortAnswersProp, shortAnswersData]);

  const { view, method, activeTab, formData, advancedControls, courseMaterials, setField } =
    useMarkingSelection(selection, onSelectionChange);
  const setView = setField('view');
  const setMethod = setField('method');
  const setActiveTab = setField('activeTab');
  const setFormData = setField('formData');
  const setAdvancedControls = setField('advancedControls');
  const setCourseMaterials = setField('courseMaterials');
  const [pendingMaterials, setPendingMaterials] = useState(0);

  const recommendations = useMemo(() => recommendMarkingTools({
    markingCriterias: rubrics,
    commentBanks,
    successCriteria,
    shortAnswers,
    context: {
      subjects: [draftSettings.subjectCode || draftSettings.subject].filter(Boolean),
      state: draftSettings.state || null,
      years: draftSettings.year ? [draftSettings.year] : [],
      taskType: draftSettings.taskType,
      responseType: draftSettings.responseType ?? 'Full Response',
      marksOutOf: resolveRecommendationMarks(
        (draftSettings.totalMarks ?? draftSettings.marksOutOf) != null,
        draftSettings.totalMarks ?? draftSettings.marksOutOf,
      ),
      hasStimulus: questionHasStimulus(draftSettings),
      questionText: draftSettings.questionText,
      introParagraphs: draftSettings.introParagraphs,
      bodyParagraphs: draftSettings.bodyParagraphs,
      conclusionParagraphs: draftSettings.conclusionParagraphs,
    },
  }), [rubrics, commentBanks, successCriteria, shortAnswers, draftSettings]);

  useEffect(() => {
    const visible = RECOMMENDABLE_TOOL_TYPES.filter((type) =>
      isFeedbackTypeVisibleForContext(type, draftSettings));
    setField('formData')((prev) => applyRecommendationsToForm(prev, recommendations, visible));
  }, [recommendations, draftSettings, setField]);

  // Preview dialog state
  const [showCommentBankPreview, setShowCommentBankPreview] = useState(false);
  const [previewCommentBank, setPreviewCommentBank] = useState(null);
  const [showSuccessCriteriaPreview, setShowSuccessCriteriaPreview] = useState(false);
  const [previewSuccessCriteria, setPreviewSuccessCriteria] = useState(null);
  const [showShortAnswerPreview, setShowShortAnswerPreview] = useState(false);
  const [previewShortAnswer, setPreviewShortAnswer] = useState(null);
  const [showMarkingCriteriaPreview, setShowMarkingCriteriaPreview] = useState(false);
  const [previewMarkingCriteria, setPreviewMarkingCriteria] = useState(null);

  const filterSubject = draftSettings.subject || null;
  const subjectTabLabel = 'General';

  const updateFormData = (patch) => {
    const types = {
      markingCriteria: FEEDBACK_TYPE_IDS.MARKING_TEMPLATES,
      commentBankId: FEEDBACK_TYPE_IDS.COMMENT_BANKS,
      commentBank: FEEDBACK_TYPE_IDS.COMMENT_BANKS,
      successCriteriaIds: FEEDBACK_TYPE_IDS.SUCCESS_CRITERIA,
      shortAnswerIds: FEEDBACK_TYPE_IDS.SHORT_ANSWERS,
    };
    const eventTools = {
      markingCriteria: 'markingCriteria',
      commentBankId: 'commentBank',
      commentBank: 'commentBank',
      successCriteriaIds: 'successCriteria',
      shortAnswerIds: 'shortAnswer',
    };
    Object.keys(patch).filter((key) => eventTools[key]).forEach((key) => {
      const value = patch[key];
      trackMarkingToolPicked(eventTools[key], value?.id ?? value, 'bulk_upload');
    });
    setFormData((prev) => ({
      ...prev,
      ...patch,
      toolAssignments: {
        ...prev.toolAssignments,
        ...Object.fromEntries(Object.keys(patch).filter((key) => types[key]).map((key) => [types[key], 'manual'])),
      },
    }));
  };

  // Preview handlers
  const handleCommentBankPreview = (id) => {
    const cb = commentBanks?.find((c) => c.id === id);
    if (cb) {
      setPreviewCommentBank(cb);
      setShowCommentBankPreview(true);
    }
  };

  const handleSuccessCriteriaPreview = (criteria) => {
    const sc = typeof criteria === 'object' ? criteria : successCriteria?.find((c) => c.id === criteria);
    if (sc) {
      setPreviewSuccessCriteria(sc);
      setShowSuccessCriteriaPreview(true);
    }
  };

  const handleShortAnswerPreview = (answer) => {
    const sa = typeof answer === 'object' ? answer : shortAnswers?.find((a) => a.id === answer);
    if (sa) {
      setPreviewShortAnswer(sa);
      setShowShortAnswerPreview(true);
    }
  };

  const handleMarkingCriteriaPreview = (mc) => {
    if (mc && Object.keys(mc).length > 0) {
      trackProductEvent('rubric_viewed', { marking_tool_id: mc.id, context: 'bulk_upload' });
      setPreviewMarkingCriteria(mc);
      setShowMarkingCriteriaPreview(true);
    }
  };

  const canSubmitCustom =
    !!formData.markingCriteria ||
    !!formData.commentBankId ||
    (formData.successCriteriaIds || []).length > 0 ||
    (formData.shortAnswerIds || []).length > 0;

  const submitJeddai = () => onSubmit?.({ mode: 'jeddai', advancedMarking: advancedControls, courseMaterials });

  const submitCustom = () =>
    onSubmit?.({
      mode: 'custom',
      markingCriteriaId: formData.markingCriteria?.id || null,
      commentBankId: formData.commentBankId || null,
      successCriteriaIds: formData.successCriteriaIds || [],
      shortAnswerIds: formData.shortAnswerIds || [],
      advancedMarking: advancedControls,
      courseMaterials,
    });

  const handleContinue = () => {
    if (view === 'method') {
      if (method === 'jeddai') setView('jeddai');
      else if (method === 'custom') setView('custom');
      return;
    }
    if (view === 'jeddai') {
      submitJeddai();
      return;
    }
    if (view === 'custom') {
      submitCustom();
    }
  };

  const continueDisabled =
    isSubmitting ||
    pendingMaterials > 0 ||
    fileCount === 0 ||
    (view === 'method' && !method) ||
    (view === 'custom' && !canSubmitCustom);

  const continueText =
    isSubmitting
      ? 'Submitting...'
      : view === 'method'
        ? 'Continue'
        : submitLabel;

  const continueIcon =
    view === 'jeddai' || view === 'custom' ? submitIcon : undefined;

  const goBack = () => {
    if (isSubmitting || pendingMaterials > 0) return;
    if (view === 'custom' || view === 'jeddai') {
      setView('method');
      return;
    }
    onBack?.();
  };

  const selectJeddai = () => {
    setMethod('jeddai');
    setView('jeddai');
  };

  const selectCustom = () => {
    setMethod('custom');
    setView('custom');
  };

  // Test data attributes
  const hasRubric = !!formData.markingCriteria;
  const hasCommentBank = !!formData.commentBankId || !!formData.commentBank;
  const hasSuccessCriteria = (formData.successCriteriaIds?.length || 0) > 0;
  const hasShortAnswers = (formData.shortAnswerIds?.length || 0) > 0;

  const materialsCard = (
    <TaskCourseMaterialsCard
      materials={courseMaterials}
      onAdd={(material) => setCourseMaterials((prev) =>
        prev.some((item) => item.id === material.id) ? prev : [...prev, material])}
      onRemove={(id) => setCourseMaterials((prev) => prev.filter((item) => item.id !== id))}
      onSaveStarted={() => setPendingMaterials((count) => count + 1)}
      onSaveSettled={() => setPendingMaterials((count) => Math.max(0, count - 1))}
    />
  );
  const tabs = (
    <TabsRow>
      <TabButton type="button" $active={activeTab === 'general'} onClick={() => setActiveTab('general')}>General</TabButton>
      <TabButton type="button" $active={activeTab === 'advanced'} onClick={() => setActiveTab('advanced')}>Advanced</TabButton>
    </TabsRow>
  );
  const advancedPanel = (
    <AdvancedPanel>
      <AdvancedMarkingControlsForm layout="nav" controls={advancedControls}
        onChange={(patch) => setAdvancedControls((prev) => ({ ...prev, ...patch }))} />
    </AdvancedPanel>
  );

  return (
    <>
    <BulkUploadWizardShell
      currentStep={currentStep}
      steps={steps}
      onBack={goBack}
      onClose={isSubmitting || pendingMaterials > 0 ? undefined : onClose}
      onStepClick={isSubmitting || pendingMaterials > 0 ? undefined : onStepClick}
      onContinue={handleContinue}
      continueLabel={continueText}
      continueIcon={continueIcon}
      isContinueDisabled={continueDisabled}
      fullPage={fullPage}
      data-component="bulk-upload-marking"
      data-unit="bulk-upload-step-5"
      data-view={view}
      data-method={method}
      data-active-tab={activeTab}
      data-has-rubric={hasRubric}
      data-has-comment-bank={hasCommentBank}
      data-has-success-criteria={hasSuccessCriteria}
      data-has-short-answers={hasShortAnswers}
      data-file-count={fileCount}
      data-continue-disabled={continueDisabled}
      data-is-submitting={isSubmitting}
    >
      {view === 'method' && (
        <MethodPanel>
          <PageTitle>Select your marking rubrics</PageTitle>
          <MethodCards>
            <MethodCard
              type="button"
              $variant="jeddai"
              $selected={method === 'jeddai'}
              onClick={selectJeddai}
            >
              <MethodIconWrap>
                <MethodIcon src={jeddaiIcon} alt="" />
              </MethodIconWrap>
              <MethodCopy>
                <MethodHeading>Let JeddAI decide</MethodHeading>
                <MethodText>{METHOD_COPY}</MethodText>
              </MethodCopy>
              <Chevron aria-hidden>›</Chevron>
            </MethodCard>

            <MethodCard
              type="button"
              $variant="custom"
              $selected={method === 'custom'}
              onClick={selectCustom}
            >
              <MethodIconWrap>
                <MethodIcon src={markingIcon} alt="" />
              </MethodIconWrap>
              <MethodCopy>
                <MethodHeading>Select your own marking rubrics</MethodHeading>
                <MethodText>Ask JeddAI to use specific marking templates and comment banks in its feedback</MethodText>
              </MethodCopy>
              <Chevron aria-hidden>›</Chevron>
            </MethodCard>
          </MethodCards>
        </MethodPanel>
      )}

      {view === 'jeddai' && (
        <CustomPanel>
          <JeddaiBackButton type="button" onClick={() => setView('method')}>
            <JeddaiBackChevron aria-hidden>‹</JeddaiBackChevron>
            Select your marking rubrics
          </JeddaiBackButton>
          <CustomCard>
            {tabs}
            <CustomBody>
            {activeTab === 'advanced' ? advancedPanel : <>
            <JeddaiConfirmCard>
            <JeddaiConfirmIcon src={jeddaiIcon} alt="" />
            <JeddaiConfirmTitle>Let JeddAI decide</JeddaiConfirmTitle>
            <JeddaiConfirmText>{METHOD_COPY}</JeddaiConfirmText>
            <JeddaiSubmitButton
              type="button"
              onClick={submitJeddai}
              disabled={continueDisabled}
            >
              <img src={submitIcon} alt="" width={24} height={24} />
              {isSubmitting ? 'Submitting...' : submitLabel}
            </JeddaiSubmitButton>
          </JeddaiConfirmCard>
            {materialsCard}
            </>}
            </CustomBody>
          </CustomCard>
        </CustomPanel>
      )}

      {view === 'custom' && (
        <CustomPanel>
          <JeddaiBackButton type="button" onClick={() => setView('method')}>
            <JeddaiBackChevron aria-hidden>‹</JeddaiBackChevron>
            Select your marking rubrics
          </JeddaiBackButton>
          <CustomCard>
            <TabsRow>
              <TabButton
                type="button"
                $active={activeTab === 'general'}
                onClick={() => setActiveTab('general')}
              >
                {subjectTabLabel}
              </TabButton>
              <TabButton
                type="button"
                $active={activeTab === 'advanced'}
                onClick={() => setActiveTab('advanced')}
              >
                Advanced
              </TabButton>
            </TabsRow>

            <CustomBody $flush={activeTab === 'advanced'}>
              {activeTab === 'general' ? (
                <>
                  <IntroBlock>
                    <IntroTitle>Use my own marking style</IntroTitle>
                    <IntroText>
                      Ask JeddAI to use specific marking templates and comment
                      banks in its feedback
                    </IntroText>
                  </IntroBlock>

                  {(draftSettings.taskType || draftSettings.subject) && (
                    <SelectedRow>
                      <SelectedLabel>Selected:</SelectedLabel>
                      <SelectedChips>
                        {draftSettings.taskType && (
                          <SelectedChip>
                            Task Type: {draftSettings.taskType}
                          </SelectedChip>
                        )}
                        {draftSettings.subject && (
                          <SelectedChip>
                            Subject: {draftSettings.subject}
                          </SelectedChip>
                        )}
                      </SelectedChips>
                    </SelectedRow>
                  )}

                  <ToolsStack>
                    <ToolSection icon={markingIcon} title="Rubrics">
                      <MarkingTemplatesFeedbackType
                        formData={formData}
                        updateFormData={updateFormData}
                        allMarkingCriterias={rubrics}
                        filterSubject={filterSubject}
                        hideGoToLink
                        showLabel={false}
                        showTooltip={false}
                        placeholder="Select rubric"
                        onMarkingCriteriaPreview={handleMarkingCriteriaPreview}
                      />
                    </ToolSection>
                    <ToolSection icon={banksIcon} title="Comment banks">
                      <CommentBanksFeedbackType
                        formData={formData}
                        updateFormData={updateFormData}
                        allCommentBanks={commentBanks}
                        filterSubject={filterSubject}
                        hideGoToLink
                        showLabel={false}
                        showTooltip={false}
                        placeholder="Select comment bank"
                        onCommentBankPreview={handleCommentBankPreview}
                      />
                    </ToolSection>
                    <ToolSection icon={darkCheckCircleIcon} title="Success criteria">
                      <SuccessCriteriaFeedbackType
                        formData={formData}
                        updateFormData={updateFormData}
                        allSuccessCriteria={successCriteria}
                        filterSubject={filterSubject}
                        hideGoToLink
                        showLabel={false}
                        showTooltip={false}
                        alwaysShowAddAnother
                        placeholder="Select success criteria"
                        onSuccessCriteriaPreview={handleSuccessCriteriaPreview}
                      />
                    </ToolSection>
                    <ToolSection icon={shortAnswerIcon} title="Short answer marking criteria">
                      <ShortAnswerFeedbackType
                        formData={formData}
                        updateFormData={updateFormData}
                        allShortAnswers={shortAnswers}
                        filterSubject={filterSubject}
                        hideGoToLink
                        showLabel={false}
                        showTooltip={false}
                        placeholder="Select short answer marking criteria"
                        onShortAnswerPreview={handleShortAnswerPreview}
                      />
                    </ToolSection>
                  </ToolsStack>
                  {materialsCard}
                </>
              ) : (
                <AdvancedPanel>
                  <AdvancedMarkingControlsForm
                    layout="nav"
                    controls={advancedControls}
                    onChange={(patch) =>
                      setAdvancedControls((prev) => ({ ...prev, ...patch }))
                    }
                  />
                </AdvancedPanel>
              )}
            </CustomBody>
          </CustomCard>
        </CustomPanel>
      )}
    </BulkUploadWizardShell>

    {/* Preview Dialogs */}
    {showCommentBankPreview && previewCommentBank && (
      <CommentBankDialog
        commentBank={previewCommentBank}
        setCommentBankPreviewDialog={setShowCommentBankPreview}
        showActionButton={false}
        showEditButton={true}
      />
    )}
    {showSuccessCriteriaPreview && previewSuccessCriteria && (
      <SuccessCriteriaDialog
        successCriteria={previewSuccessCriteria}
        setSuccessCriteriaPreviewDialog={setShowSuccessCriteriaPreview}
        showActionButton={false}
        showEditButton={true}
      />
    )}
    {showShortAnswerPreview && previewShortAnswer && (
      <ShortAnswerDialog
        shortAnswer={previewShortAnswer}
        setShortAnswerPreviewDialog={setShowShortAnswerPreview}
        showActionButton={false}
        showEditButton={true}
      />
    )}
    {showMarkingCriteriaPreview && previewMarkingCriteria && (
      <PreviewDialog
        markingCriterias={previewMarkingCriteria}
        setMarkingCriteriaPreviewDialog={setShowMarkingCriteriaPreview}
        showEditButton={true}
      />
    )}
  </>
  );
};

const MethodPanel = styled.div`
  flex: 1;
  align-self: stretch;
  min-height: 0;
  min-width: 0;
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 24px;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #7200e0 transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #7200e0;
    border-radius: 4px;
  }
`;

const PageTitle = styled.h2`
  margin: 0;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 18px;
  font-weight: 600;
  color: #1e252a;
  text-align: center;
`;

const MethodCards = styled.div`
  width: 100%;
  max-width: 578px;
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

const MethodCard = styled.button`
  width: 100%;
  box-sizing: border-box;
  display: flex;
  align-items: flex-start;
  gap: 16px;
  padding: 20px;
  border-radius: 8px;
  border: 1.5px solid
    ${({ $variant }) => ($variant === 'jeddai' ? '#088AAF' : '#7200E0')};
  background: ${({ $variant }) =>
    $variant === 'jeddai' ? '#F7FCFD' : '#FBF7FE'};
  text-align: left;
  cursor: pointer;
  position: relative;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  outline: none;

  &:hover {
    border-color: ${({ $variant }) =>
      $variant === 'jeddai' ? '#088AAF' : '#7200E0'};
    background: ${({ $variant }) =>
      $variant === 'jeddai' ? '#F7FCFD' : '#FBF7FE'};
  }
`;

const MethodIconWrap = styled.span`
  width: 36px;
  height: 36px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-top: 2px;
  opacity: ${({ $muted }) => ($muted ? 0.85 : 1)};
`;

const MethodIcon = styled.img`
  width: 36px;
  height: 36px;
  object-fit: contain;
`;

const MethodCopy = styled.span`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;
  min-width: 0;
  padding-right: 24px;
`;

const MethodHeading = styled.span`
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
  color: #1e252a;
`;

const MethodText = styled.span`
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  font-weight: 400;
  line-height: 1.45;
  color: #6b7280;
`;

const Chevron = styled.span`
  position: absolute;
  right: 18px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 24px;
  color: #4b464f;
  line-height: 1;
`;

const CustomPanel = styled.div`
  flex: 1;
  align-self: stretch;
  min-height: 0;
  min-width: 0;
  padding: 24px 28px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  overflow-x: hidden;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: #7200e0 transparent;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background: #7200e0;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #5b00b5;
  }
`;

const CustomCard = styled.div`
  width: 100%;
  max-width: 900px;
  flex-shrink: 0;
  border: 1px solid rgba(201, 198, 204, 0.55);
  border-radius: 8px;
  background: #fff;
  box-shadow: 0 4px 9px rgba(133, 133, 133, 0.12);
`;

const TabsRow = styled.div`
  display: flex;
  gap: 24px;
  padding: 0 28px;
  border-bottom: 1px solid rgba(201, 198, 204, 0.5);
`;

const TabButton = styled.button`
  border: none;
  background: none;
  padding: 18px 0;
  cursor: pointer;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 16px;
  font-weight: ${({ $active }) => ($active ? 500 : 400)};
  color: ${({ $active }) => ($active ? '#7200e0' : '#7b7382')};
  border-bottom: 2px solid ${({ $active }) => ($active ? '#7200e0' : 'transparent')};
  margin-bottom: -1px;
`;

const CustomBody = styled.div`
  padding: ${({ $flush }) => ($flush ? '20px 28px 24px' : '24px 28px')};
  display: flex;
  flex-direction: column;
  gap: 24px;
`;

const IntroBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const IntroTitle = styled.h3`
  margin: 0;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 18px;
  font-weight: 600;
  color: #1e252a;
`;

const IntroText = styled.p`
  margin: 0;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  line-height: 1.45;
  color: #6b7280;
`;

const ToolsStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const ToolSection = ({ icon, title, children }) => (
  <ToolSectionRoot>
    <ToolSectionHeader>
      <ToolSectionIcon src={icon} alt="" />
      <ToolSectionTitle>{title}</ToolSectionTitle>
    </ToolSectionHeader>
    <ToolSectionBody>{children}</ToolSectionBody>
  </ToolSectionRoot>
);

const ToolSectionRoot = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const ToolSectionHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const ToolSectionIcon = styled.img`
  width: 24px;
  height: 24px;
  object-fit: contain;
`;

const ToolSectionTitle = styled.span`
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 16px;
  font-weight: 600;
  color: #1e252a;
`;

const ToolSectionBody = styled.div`
  padding-left: 36px;
`;

const SelectedRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 10px;
`;

const SelectedLabel = styled.span`
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  font-weight: 600;
  color: #1e252a;
`;

const SelectedChips = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

const SelectedChip = styled.span`
  display: inline-flex;
  align-items: center;
  padding: 4px 12px;
  border: 1px solid #e5e5e5;
  border-radius: 999px;
  background: #fff;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 13px;
  font-weight: 500;
  color: #4b464f;
`;

const AdvancedPanel = styled.div`
  width: 100%;
`;

const JeddaiPanel = styled.div`
  flex: 1;
  align-self: stretch;
  min-height: 0;
  width: 100%;
  box-sizing: border-box;
  background: #f2f1f3;
  overflow-y: auto;
  padding: 20px 28px 40px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 32px;
`;

const JeddaiBackButton = styled.button`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  border: none;
  background: transparent;
  padding: 0;
  cursor: pointer;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  color: #7200e0;

  &:hover {
    opacity: 0.85;
  }
`;

const JeddaiBackChevron = styled.span`
  font-size: 22px;
  line-height: 1;
  margin-top: -1px;
`;

const JeddaiConfirmCard = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  padding: 36px 40px 32px;
  background: #fff;
  text-align: center;
  box-sizing: border-box;
`;

const JeddaiConfirmIcon = styled.img`
  width: 36px;
  height: 36px;
  object-fit: contain;
`;

const JeddaiConfirmTitle = styled.h3`
  margin: 0;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 18px;
  font-weight: 600;
  color: #1e252a;
`;

const JeddaiConfirmText = styled.p`
  margin: 0;
  max-width: 380px;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 14px;
  line-height: 1.45;
  color: #4b464f;
`;

const JeddaiSubmitButton = styled.button`
  margin-top: 8px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  min-height: 44px;
  padding: 10px 28px;
  border: none;
  border-radius: 999px;
  background: #7200e0;
  color: #fff;
  font-family: var(--font-family-ibm_plex_sans, 'IBM Plex Sans', sans-serif);
  font-size: 16px;
  font-weight: 500;
  line-height: 24px;
  cursor: pointer;

  img {
    width: 24px;
    height: 24px;
    display: block;
  }

  &:hover:not(:disabled) {
    background: #5b00b5;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;
